import BubbleBackground from "../common/BubbleBackground";
import LeftCurveLine from "../../images/LeftCurveLine";
import RightCurveLine from "../../images/RightCurveLine";
import StraightLine from "../../images/StraightLine";
import SuccessIcon from "../../images/SuccessIcon";
import celebrationGif from "../../images/celebration_animation.gif";
import LockIcon from "../../images/LockIcon";
import UnlockIcon from "../../images/UnlockIcon";
import { LessonTimeLine } from "../../types/newtypes";
import HalfLine from "../../images/HalfLine";
import TrophyIcon from "../../images/TrophyIcon";
import { useLessonTimeline, UseLessonTimelineProps } from "./useLessonTimeline";
import { BookOpen } from "lucide-react";
import Typography from "../common/Typography";
import { useNavigate } from "react-router-dom";

export interface LessonTimelineProps {
  lessons: LessonTimeLine[];
  onLessonClick: UseLessonTimelineProps["onLessonClick"];
  courseId: string;
  completedLessons?: number;
  showCertificateAction?: boolean;
}

const LessonTimeline = ({
  lessons,
  onLessonClick,
  completedLessons = 0,
  showCertificateAction = true,
}: LessonTimelineProps) => {
  const navigate = useNavigate();
  const {
    allItems,
    isLessonAvailable,
    isLessonComplete,
    allLessonsCompleted,
    activeLessonId,
    clickedLesson,
    showCelebration,
    showUnlockIcon,
    activeBubbleId,
    handleLessonClick,
    getBubblePosition,
    getBubbleContent,
    initialConnectorActive,
  } = useLessonTimeline({
    lessons,
    completedLessons,
    onLessonClick,
  });

  const handleTrophyClick = () => {
    if (!allLessonsCompleted || !showCertificateAction) {
      return;
    }

    navigate("/certificates");
  };

  // Optional units are still units, so they consume a number. The synthetic
  // trophy is absent from this map and therefore never receives one.
  const unitNumberById = new Map(
    lessons.map((lesson, index) => [lesson.id, index + 1]),
  );

  const renderLessons = () => {
    if (lessons.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-20 px-8 text-center bg-white">
          <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
            <BookOpen className="w-10 h-10 text-indigo-500" />
          </div>
          <Typography
            variant="h4"
            weight="bold"
            className="text-textPrimary mb-3"
          >
            No Units Available
          </Typography>
          <Typography
            variant="p"
            className="text-textPrimary-grey max-w-sm leading-relaxed"
          >
            We're working hard to create amazing content for this module. Check
            back soon for exciting new lessons!
          </Typography>
          <div className="mt-6 flex items-center gap-2 text-sm text-indigo-500">
            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
            <Typography variant="small" className="text-indigo-500">
              Coming soon
            </Typography>
          </div>
        </div>
      );
    }

    const rows = Math.ceil(allItems.length / 2);
    const lessonRows = [];

    // Add the left panel connector - active fill path for the first lesson

    for (let i = 0; i < rows; i++) {
      const isEven = i % 2 === 0;
      const rowStartIndex = i * 2;
      const leftLesson = isEven
        ? allItems[rowStartIndex]
        : allItems[rowStartIndex + 1];
      const rightLesson = isEven
        ? allItems[rowStartIndex + 1]
        : allItems[rowStartIndex];
      const isLeftLessonCompleted = leftLesson
        ? isLessonComplete(leftLesson.id)
        : false;
      const isRightLessonCompleted = rightLesson
        ? isLessonComplete(rightLesson.id)
        : false;

      const isStraightActive =
        (isLeftLessonCompleted || isRightLessonCompleted) &&
        (rowStartIndex === 0 ||
          (allItems[rowStartIndex - 2] &&
            isLessonComplete(allItems[rowStartIndex - 2].id)));
      const isLeftActive =
        isLeftLessonCompleted &&
        (rowStartIndex === 0 ||
          (allItems[rowStartIndex - 1] &&
            isLessonComplete(allItems[rowStartIndex - 1].id)));

      const isRightActive = isRightLessonCompleted && isLeftLessonCompleted;

      // For rows where we need to check diagonal connections
      const isRightDiagonalActive =
        i > 0 &&
        isRightLessonCompleted &&
        allItems[rowStartIndex - 1] &&
        isLessonComplete(allItems[rowStartIndex - 1].id);

      // Special case: if this is the last row and only has trophy, use grid-cols-1
      const isTrophyRow =
        i === rows - 1 && !rightLesson && leftLesson?.id === "trophy";

      // Check if trophy is anywhere in this row (for consistent line hiding)
      const hasTrophy =
        leftLesson?.id === "trophy" || rightLesson?.id === "trophy";

      lessonRows.push(
        <div
          key={`row-${i}`}
          className={`relative ${isTrophyRow ? "grid-cols-1" : "grid-cols-2"} grid justify-between w-full gap-5 mb-16`}
          style={{
            marginTop: i === 0 ? "100px" : isTrophyRow ? "24px" : "47px",
          }}
        >
          {hasTrophy ? // No line connecting to trophy - keeps visual clean regardless of lesson count
            null : (
              <StraightLine
                className="absolute top-8 w-full mx-auto left-auto right-auto"
                isActive={isStraightActive}
                isEven={isEven}
              />
            )}

          {/* Left lesson node */}
          {leftLesson && (
            <div
              className={`flex left-lesson ${isTrophyRow ? "mx-auto" : "w-fit mx-auto"} flex-col items-center`}
            >
              {/* Hide curve line if this is a trophy or if trophy is alone in row */}
              {!isTrophyRow && leftLesson.id !== "trophy" && (
                <LeftCurveLine
                  className="left-curve-line absolute top-8 left-0"
                  isActive={isLeftActive}
                />
              )}

              {/* Bubble for left lesson */}
              {activeBubbleId === leftLesson.id && (
                <div
                  className={`pointer-events-none absolute z-20 ${getBubblePosition(leftLesson.id)}`}
                >
                  <BubbleBackground
                    title={getBubbleContent(leftLesson.id).title}
                    subtitle={getBubbleContent(leftLesson.id).subtitle}
                  />
                </div>
              )}

              <div className="relative flex items-center justify-center">
                {showCelebration === leftLesson.id && (
                  <img
                    className="absolute flex aspect-square min-w-[160px] -z-10 h-auto m-auto"
                    src={celebrationGif}
                    alt="animation gif"
                  />
                )}
                {leftLesson.id === "trophy" ? (
                  <button
                    type="button"
                    role={showCertificateAction ? undefined : "img"}
                    aria-label={
                      showCertificateAction
                        ? "View My Certificates"
                        : allLessonsCompleted
                          ? "Module completed"
                          : "Course incomplete"
                    }
                    onClick={handleTrophyClick}
                    disabled={!allLessonsCompleted || !showCertificateAction}
                    className={`relative flex items-center justify-center w-16 h-16 rounded-full transition-all duration-300 border-2
                    ${
                  allLessonsCompleted
                    ? "border-primary bg-grey-0"
                    : "border-gray-300 bg-white grayscale"
                  }
                    ${allLessonsCompleted ? "scale-110 animate-[bounce_1s_ease-in-out]" : ""}`}
                  >
                    <TrophyIcon
                      className={
                        allLessonsCompleted ? "opacity-100" : "opacity-30"
                      }
                      width={26}
                    />
                  </button>
                ) : (
                  <button
                    id={`lesson-${leftLesson.id}`}
                    onClick={(e) => handleLessonClick(e, leftLesson)}
                    className={`relative overflow-hidden flex items-center justify-center w-16 h-16 rounded-full transition-all duration-300 border-2 
                    ${
                  isLessonComplete(leftLesson.id)
                    ? "bg-primary !border-0"
                    : isLessonAvailable(leftLesson.id)
                      ? "bg-white border-primary scale-150"
                      : "bg-gray-100 border-gray-300"
                  }
                    ${leftLesson.id === activeLessonId ? " scale-110" : ""}
                    ${clickedLesson === leftLesson.id ? "scale-95" : ""}`}
                    disabled={!isLessonAvailable(leftLesson.id)}
                  >
                    {isLessonComplete(leftLesson.id) ? (
                      <SuccessIcon className="w-[58px] h-[58px]" />
                    ) : showUnlockIcon === leftLesson.id ? (
                      <UnlockIcon />
                    ) : isLessonAvailable(leftLesson.id) ? (
                      <span className="text-primary text-xl">📖</span>
                    ) : (
                      <LockIcon />
                    )}
                  </button>
                )}
              </div>
              <div
                className={`mt-3 bg-white px-4 py-2 rounded-lg shadow-sm border transition-colors duration-300
                ${
            leftLesson.id === "trophy"
              ? allLessonsCompleted
                ? "border-primary text-textPrimary"
                : "border-gray-200 text-gray-400"
              : isLessonComplete(leftLesson.id)
                ? "border-grey-300 text-textPrimary"
                : isLessonAvailable(leftLesson.id)
                  ? "border-primary text-textPrimary translate-y-4"
                  : "border-grey-300 text-gray-400"
            }
                ${leftLesson.id === activeLessonId ? "bg-green-50" : ""} ${leftLesson.id === "trophy" ? "sm:block hidden" : "block"}`}
              >
                <span className="font-medium block max-w-[26rem] whitespace-normal break-words text-center leading-snug">
                  {leftLesson.id === "trophy" ? (
                    leftLesson.title
                  ) : (
                    <>
                      <span data-testid="timeline-unit-number">
                        Unit {unitNumberById.get(leftLesson.id)}
                      </span>
                      <span className="hidden sm:inline">
                        : {leftLesson.title}
                      </span>
                    </>
                  )}
                </span>
                {leftLesson.isOptional && (
                  <span
                    data-testid="timeline-optional-caption"
                    className="mt-1 block text-center text-xs text-indigo-700"
                  >
                    Optional for non-teaching staff
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Right lesson node */}
          {rightLesson && (
            <div className="flex right-lesson w-fit mx-auto flex-col items-center">
              {/* Hide curve line if this is a trophy */}
              {rightLesson.id !== "trophy" && (
                <RightCurveLine
                  className="right-curve-line absolute top-8 right-0"
                  isActive={isRightActive || isRightDiagonalActive}
                />
              )}

              {/* Bubble for right lesson */}
              {activeBubbleId === rightLesson.id && (
                <div
                  className={`pointer-events-none absolute z-20 scale-x-[-1] ${getBubblePosition(rightLesson.id)}`}
                >
                  <BubbleBackground
                    title={getBubbleContent(rightLesson.id).title}
                    subtitle={getBubbleContent(rightLesson.id).subtitle}
                    bubbleSide="right"
                  />
                </div>
              )}

              <div className="relative flex items-center justify-center">
                {/* Show celebration only when explicitly set */}
                {showCelebration === rightLesson.id && (
                  <img
                    className="absolute flex aspect-square min-w-[160px] -z-10 h-auto m-auto"
                    src={celebrationGif}
                    alt="animation gif"
                  />
                )}
                {rightLesson.id === "trophy" ? (
                  <button
                    type="button"
                    role={showCertificateAction ? undefined : "img"}
                    aria-label={
                      showCertificateAction
                        ? "View My Certificates"
                        : allLessonsCompleted
                          ? "Module completed"
                          : "Course incomplete"
                    }
                    onClick={handleTrophyClick}
                    disabled={!allLessonsCompleted || !showCertificateAction}
                    className={`relative flex items-center justify-center w-16 h-16 rounded-full transition-all duration-300 border-2
                    ${
                  allLessonsCompleted
                    ? "border-primary bg-grey-0"
                    : "border-gray-300 bg-white"
                  }
                    ${allLessonsCompleted ? "scale-110 animate-[bounce_1s_ease-in-out]" : ""}`}
                  >
                    <TrophyIcon width={26} />
                  </button>
                ) : (
                  <button
                    id={`lesson-${rightLesson.id}`}
                    onClick={(e) => handleLessonClick(e, rightLesson)}
                    className={`flex items-center overflow-hidden justify-center w-16 h-16 rounded-full transition-all duration-300 border-2 
                    ${
                  isLessonComplete(rightLesson.id)
                    ? "bg-primary !border-0"
                    : isLessonAvailable(rightLesson.id)
                      ? "bg-white border-primary scale-150"
                      : "bg-gray-100 border-gray-300"
                  }
                    ${rightLesson.id === activeLessonId ? " scale-110" : ""}
                    ${clickedLesson === rightLesson.id ? "scale-95" : ""}`}
                    disabled={!isLessonAvailable(rightLesson.id)}
                  >
                    {isLessonComplete(rightLesson.id) ? (
                      <SuccessIcon className="w-[58px] h-[58px]" />
                    ) : showUnlockIcon === rightLesson.id ? (
                      <UnlockIcon />
                    ) : isLessonAvailable(rightLesson.id) ? (
                      <span className="text-primary text-xl">📖</span>
                    ) : (
                      <LockIcon />
                    )}
                  </button>
                )}
              </div>
              <div
                className={`mt-3 bg-white px-4 py-2 rounded-lg shadow-sm border transition-colors duration-300
                ${
            rightLesson.id === "trophy"
              ? allLessonsCompleted
                ? "border-primary text-textPrimary"
                : "border-gray-200 text-gray-400"
              : isLessonComplete(rightLesson.id)
                ? "border-grey-300 text-textPrimary"
                : isLessonAvailable(rightLesson.id)
                  ? "border-primary text-textPrimary translate-y-4"
                  : "border-grey-300 text-gray-400"
            }
                ${rightLesson.id === activeLessonId ? "bg-green-50" : ""} ${rightLesson.id === "trophy" ? "sm:block hidden" : "block"}`}
              >
                <span className="font-medium block max-w-[26rem] whitespace-normal break-words text-center leading-snug">
                  {rightLesson.id === "trophy" ? (
                    rightLesson.title
                  ) : (
                    <>
                      <span data-testid="timeline-unit-number">
                        Unit {unitNumberById.get(rightLesson.id)}
                      </span>
                      <span className="hidden sm:inline">
                        : {rightLesson.title}
                      </span>
                    </>
                  )}
                </span>
                {rightLesson.isOptional && (
                  <span
                    data-testid="timeline-optional-caption"
                    className="mt-1 block text-center text-xs text-indigo-700"
                  >
                    Optional for non-teaching staff
                  </span>
                )}
              </div>
            </div>
          )}
        </div>,
      );
    }

    return lessonRows;
  };

  return (
    <div className="lg:col-span-2">
      <div className="bg-transparent rounded-xl p-6">
        {/* Timeline */}
        <div className="relative">
          {/* Initial connector between left panel and first lesson */}
          {initialConnectorActive && (
            <HalfLine className="absolute -left-8 top-[132px] z-10" isActive />
          )}
          <div className="relative z-10 grid grid-cols-1 gap-4 odd:[&_div:nth-child(odd)_.left-lesson_.left-curve-line]:hidden odd:[&_div:nth-child(even)_.right-lesson_.right-curve-line]:hidden [&_>_div:last-child_.right-curve-line]:hidden [&_>_div:last-child_.left-curve-line]:hidden">
            {/* Lesson nodes grid */}
            {renderLessons()}
          </div>
          {lessons.some((lesson) => lesson.isOptional) && (
            <p
              data-testid="timeline-optional-footnote"
              className="mt-6 text-center text-sm text-slate-600"
            >
              Units marked optional for non-teaching staff do not affect
              certificate eligibility.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonTimeline;
